import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAnalytics } from 'use-analytics';
import { z } from 'zod';
import { QueryKey } from '@/Config/api/queryKey';
import i18n from '@/Config/app/i18n';
import type { LoginResponse } from '@/Domain/api/User/dto/User.dto';
import { completeLogin } from '@/Domain/hooks/Auth/completeLogin';
import { useCaptchaFallback } from '@/Domain/hooks/Auth/useCaptchaFallback.hook';
import errorMapper, { Error, ErrorCodes } from '@/Domain/utils/errorMapper';
import {
  clearPendingVerificationEmail,
  setPendingVerificationEmail,
} from '@/Domain/utils/pendingVerificationEmail';

const createLoginValidator = () =>
  z.object({
    username: z.string().email(i18n.t('validation:invalid_email')),
    password: z.string().min(1, i18n.t('validation:password_required')),
    recaptcha_response_v3: z.string().optional(),
    recaptcha_response_v2: z.string().optional(),
  });

export type LoginForm = z.infer<ReturnType<typeof createLoginValidator>>;

export const useLogin = () => {
  const { t } = useTranslation('validation');
  const { track } = useAnalytics();
  const postHog = usePostHog();
  const navigate = useNavigate();
  const { search } = useLocation();

  const [isPending, setIsPending] = useState(false);
  const [tooManyRequests, setTooManyRequests] = useState<boolean>(false);
  const [lockoutMinutes, setLockoutMinutes] = useState<number | null>(null);
  const [showGoogleHint, setShowGoogleHint] = useState<boolean>(false);
  const [showPasskeyHint, setShowPasskeyHint] = useState<boolean>(
    () => new URLSearchParams(search).get('login_error') === ErrorCodes.PASSKEY_REQUIRED,
  );

  const {
    control,
    formState: { errors },
    handleSubmit,
    getValues,
    setError,
    clearErrors,
    setValue,
    register,
  } = useForm<LoginForm>({
    resolver: zodResolver(createLoginValidator()),
  });

  register('recaptcha_response_v3');

  const {
    handleCaptchaError,
    handleSubmissionError,
    escalateToV2,
    handleV2Focus,
    isPreparingSubmission,
    prepareCaptchaSubmission,
    shouldFocusV2,
    useV2,
  } = useCaptchaFallback({
    clearSubmissionErrors: () => {
      clearErrors('root');
      clearErrors('recaptcha_response_v2');
    },
    hasV2Token: () => !!getValues('recaptcha_response_v2'),
    resetV2Token: () => setValue('recaptcha_response_v2', ''),
    setGenericError: () => setError('root', { message: i18n.t('login:error_generic') }),
    setRequiredV2Error: () => setError('recaptcha_response_v2', { type: 'required' }),
  });

  const mutation = useMutation<LoginResponse, Error<LoginForm>, LoginForm>({
    mutationKey: [QueryKey.accounts.login],
    throwOnError: false,
    onSuccess: (response) => completeLogin(response, postHog, 'password'),
    onError: (error) => {
      handleSubmissionError();
      setIsPending(false);
      track('login_form_error', { event_category: 'User login', event_label: 'login_form' });

      const fieldErrors = error.response?.data?.errors;
      if (
        (error.response?.status ?? 0) >= 500 ||
        !fieldErrors ||
        !Object.keys(fieldErrors).length
      ) {
        setError('root', { message: i18n.t('login:error_generic') });
        return;
      }

      // eslint-disable-next-line
      errorMapper(fieldErrors, (field, message, code, options) => {
        if (handleCaptchaError(field)) {
          return;
        }

        if (code && code === ErrorCodes.TOO_MANY_REQUESTS) {
          setTooManyRequests(true);
          const retryAfterSeconds = options?.retry_after_seconds;
          setLockoutMinutes(
            typeof retryAfterSeconds === 'number'
              ? Math.max(1, Math.ceil(retryAfterSeconds / 60))
              : null,
          );
          return;
        }

        if (code === ErrorCodes.NOT_ACTIVATED) {
          const userEmail = options?.user_email;
          if (userEmail) {
            setPendingVerificationEmail(userEmail);
          } else {
            // Drop any address left by an earlier signup so resend can't target the wrong account.
            clearPendingVerificationEmail();
          }
          navigate('/register/verify', { viewTransition: true });
          return;
        }

        if (code === ErrorCodes.USE_GOOGLE_SIGNIN) {
          setShowGoogleHint(true);
          return;
        }

        if (code === ErrorCodes.PASSKEY_REQUIRED) {
          setShowPasskeyHint(true);
          return;
        }

        setError('username', { message: t('invalid_credentials') });
        setError('password', { message: t('invalid_credentials') });
      });
    },
  });

  const onSubmit = async (data: LoginForm) => {
    const payload = await prepareCaptchaSubmission(data);
    if (!payload) {
      return;
    }

    track('login_form_submit', { event_category: 'User login', event_label: 'login_form' });
    setIsPending(true);
    setTooManyRequests(false);
    setLockoutMinutes(null);
    setShowGoogleHint(false);
    setShowPasskeyHint(false);
    return mutation.mutate(payload);
  };

  const onVerify = useCallback(
    (token: string) => {
      setValue('recaptcha_response_v3', token);
    },
    [setValue],
  );

  return {
    control,
    escalateToV2,
    handleV2Focus,
    shouldFocusV2,
    useV2,
    register,
    errors,
    handleSubmit,
    onSubmit,
    onVerify,
    isPending: isPending || isPreparingSubmission,
    tooManyRequests,
    lockoutMinutes,
    showGoogleHint,
    showPasskeyHint,
  };
};
